/**
 * Market sizing — TAM / SAM / SOM for the investor deck.
 *
 * Per-firm ROI lives in `./data.ts`; this module is the market-level view.
 * Figures supplied by the client (August 2026). Single source of truth: edit the
 * constants here and totals/labels flow through to the UI.
 *
 * All amounts are in millions of GBP ARR.
 */

export interface MarketSegment {
  label: string;
  /** ARR in £m. */
  arr: number;
  /** Optional context shown directly beneath a market label. */
  detail?: string;
  /** Firm count, when the segment is part of the UK I&R market table. */
  firms?: number;
  /** Typical annual licenses sold to each firm in this segment. */
  licensesPerFirm?: number;
  /** Annual fee charged for each license in GBP. */
  annualLicenseFee?: number;
  /** Blended annual contract value in GBP. */
  averageAcv?: number;
  /** Fixed decimal places required by the approved table presentation. */
  displayDecimals?: number;
}

export interface MarketGroup {
  /** Short heading for the group. */
  label: string;
  segments: MarketSegment[];
}

export interface MarketTier {
  id: 'tam' | 'sam' | 'som' | 'intl';
  /** Three-letter acronym, displayed prominently. */
  acronym: string;
  /** Full title, e.g. "Total Addressable Market". */
  title: string;
  /** Sub-heading that frames the scope (one short phrase). */
  scope: string;
  /** Headline ARR figure as a display string for non-animated contexts. */
  headline: string;
  /** When set, the numeric portion animates from 0 → this value on entry.
      The display renders as `~£{value}m ARR`. */
  headlineValue?: number;
  /** For range headlines like "£40m–50m": both endpoints animate from 0.
      The display renders as `~£{low}m–{high}m ARR`. */
  headlineRange?: [low: number, high: number];
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  TAM — Total Addressable Market (£48.82m ARR, rounded to £50m)            */
/* ──────────────────────────────────────────────────────────────────────── */

export const TAM_GROUPS: readonly MarketGroup[] = [
  {
    label: 'UK I&R Market',
    segments: [
      createUkMarketSegment('Enterprise (10+ IPs)', 21, 350, 2_750),
      createUkMarketSegment('Mid-tier (2–10 IPs)', 84, 48, 3_750),
      createUkMarketSegment('Small firms', 355, 8, 4_750),
    ],
  },
];

export const UK_TAM_ARR = roundToTwo(tamTotal());
export const UK_TAM_HEADLINE_ARR = 50;
export const UK_SAM_ARR = 32;
export const UK_SOM_ARR = 4.5;

export const TAM_TIER: MarketTier = {
  id: 'tam',
  acronym: 'TAM',
  title: 'Total Addressable Market',
  scope: 'Entire UK insolvency and restructuring software market',
  headline: `~${formatARR(UK_TAM_HEADLINE_ARR)} ARR`,
  headlineValue: UK_TAM_HEADLINE_ARR,
};

/* ──────────────────────────────────────────────────────────────────────── */
/*  International expansion — separate tab                                  */
/* ──────────────────────────────────────────────────────────────────────── */

export const INTERNATIONAL_EXPANSION_GROUP: MarketGroup = {
  label: 'International Expansion Markets',
  segments: [
    { label: 'Australia', arr: 37 },
    { label: 'Canada', arr: 44 },
    { label: 'Hong Kong', arr: 23 },
    { label: 'UAE', arr: 17, detail: 'DIFC / ADGM' },
    { label: 'Singapore', arr: 12 },
    { label: 'Ireland', arr: 13 },
    { label: 'New Zealand', arr: 8 },
    { label: 'Cayman Islands', arr: 6 },
    { label: 'British Virgin Islands', arr: 5 },
    { label: 'Channel Islands', arr: 3, detail: 'Indicative estimate' },
  ],
};

export function internationalTotal(): number {
  return groupTotal(INTERNATIONAL_EXPANSION_GROUP);
}

export const INTERNATIONAL_TIER: MarketTier = {
  id: 'intl',
  acronym: 'INT',
  title: 'International expansion',
  scope: 'Indicative priority expansion markets beyond the UK',
  headline: `${formatARR(internationalTotal())} ARR`,
  headlineValue: internationalTotal(),
};

/* ──────────────────────────────────────────────────────────────────────── */
/*  SAM — Serviceable Addressable Market (£32m ARR)                          */
/* ──────────────────────────────────────────────────────────────────────── */

export const SAM_TIER: MarketTier = {
  id: 'sam',
  acronym: 'SAM',
  title: 'Serviceable Addressable Market',
  scope: 'UK firms realistically targetable in first 5 to 7 years',
  headline: `${formatARR(UK_SAM_ARR)} ARR`,
  headlineValue: UK_SAM_ARR,
};

export interface MarketAudience {
  /** Heading above the bullet list (e.g. "Focus market", "Initial commercial focus"). */
  heading: string;
  items: string[];
}

export const SAM_AUDIENCE: MarketAudience = {
  heading: 'Focus market',
  items: [
    'Top 30 insolvency firms',
    'Mid-tier insolvency firms',
    'Specialist boutiques',
  ],
};

/* ──────────────────────────────────────────────────────────────────────── */
/*  SOM — Serviceable Obtainable Market (£4.5m ARR)                          */
/* ──────────────────────────────────────────────────────────────────────── */

export const SOM_TIER: MarketTier = {
  id: 'som',
  acronym: 'SOM',
  title: 'Serviceable Obtainable Market',
  scope: 'Realistic market capture within first 3 years',
  headline: `${formatARR(UK_SOM_ARR)} ARR`,
  headlineValue: UK_SOM_ARR,
};

export const SOM_AUDIENCE: MarketAudience = {
  heading: 'Initial commercial focus',
  items: [
    '75–120 firms onboarded',
    'Enterprise + mid-market focus',
    'AI premium modules + workflow automation',
  ],
};

/* ──────────────────────────────────────────────────────────────────────── */
/*  Derived totals                                                           */
/* ──────────────────────────────────────────────────────────────────────── */

export function groupTotal(group: MarketGroup): number {
  return group.segments.reduce((sum, s) => sum + s.arr, 0);
}

export function groupFirmTotal(group: MarketGroup): number {
  return group.segments.reduce((sum, segment) => sum + (segment.firms ?? 0), 0);
}

export function tamTotal(): number {
  return roundToTwo(
    TAM_GROUPS.reduce((sum, group) => sum + groupTotal(group), 0),
  );
}

export function tamFirmTotal(): number {
  return TAM_GROUPS.reduce((sum, group) => sum + groupFirmTotal(group), 0);
}

/**
 * Format an £m ARR figure for display.
 * Whole numbers drop the decimal: `22` → `"£22m"`; `12.5` → `"£12.5m"`.
 */
export function formatARR(arrMillions: number, fixedDecimals?: number): string {
  const rounded = roundToTwo(arrMillions);

  if (fixedDecimals !== undefined) {
    return `£${rounded.toFixed(fixedDecimals)}m`;
  }

  const isWhole = Number.isInteger(rounded);
  const body = isWhole
    ? rounded.toFixed(0)
    : rounded.toFixed(2).replace(/0$/, '');
  return `£${body}m`;
}

export function formatAcv(acvPounds: number): string {
  return `£${Math.round(acvPounds / 1_000).toLocaleString('en-GB')}k`;
}

export function formatPounds(amount: number): string {
  return `£${amount.toLocaleString('en-GB')}`;
}

function createUkMarketSegment(
  label: string,
  firms: number,
  licensesPerFirm: number,
  annualLicenseFee: number,
): MarketSegment {
  const averageAcv = licensesPerFirm * annualLicenseFee;

  return {
    label,
    firms,
    licensesPerFirm,
    annualLicenseFee,
    averageAcv,
    arr: (firms * averageAcv) / 1_000_000,
    displayDecimals: 2,
  };
}

function roundToTwo(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
